import * as React from 'react';
import FlowGraph from 'components/FlowGraph';
import useBucketedFlowData, {
  BucketSizeValue,
  FlowMetric,
  getTradeDateCount,
  BUCKET_SIZE_OPTIONS,
  MULTI_DAY_BUCKET_OPTIONS,
  DEFAULT_SINGLE_DAY_BUCKET,
  DEFAULT_MULTI_DAY_BUCKET,
} from 'hooks/useBucketedFlowData';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { backfillLeadingPrices } from 'utils/shared/trades/aggregateFlowBuckets';
import resolveUserTimezone from 'utils/shared/time/resolveUserTimezone';
import { Trade } from 'utils/shared/trades/types';

// Market-wide reference symbol for the right-axis price line on the Flow page.
// Trades for this ticker are present in the option_flow feed, so we derive the
// price series from the same dataset rather than fetching index bars separately.
const REFERENCE_TICKER = 'SPX';

interface Props {
  trades: Trade[];
  // Unfiltered trades used only to derive the SPX reference price series.
  // Falls back to `trades` when not provided. Keeping it separate means the
  // reference line stays visible even when the table is filtered to a preset
  // (e.g. EQUITIES) that excludes SPX.
  referenceTrades?: Trade[];
}

export default function FlowOptionGraphView({ trades, referenceTrades }: Props) {
  const { user: currentUser } = useCurrentUser();
  const displayTimezone = React.useMemo(
    () => resolveUserTimezone(currentUser?.timezoneLocation),
    [currentUser?.timezoneLocation],
  );

  const [metric, setMetric] = React.useState<FlowMetric>('premium');
  const [userBucketSize, setUserBucketSize] = React.useState<BucketSizeValue>(
    DEFAULT_SINGLE_DAY_BUCKET,
  );
  const [mode, setMode] = React.useState<'total' | 'net'>('total');

  const isMultiDay = React.useMemo(() => getTradeDateCount(trades) > 1, [trades]);
  const bucketOptions = isMultiDay ? MULTI_DAY_BUCKET_OPTIONS : BUCKET_SIZE_OPTIONS;

  // Derive the effective bucket on render so an incompatible user pick is
  // corrected immediately (no extra render via useEffect → setState). The
  // user's pick stays in `userBucketSize` and resurfaces if the data shape
  // flips back.
  const bucketSize = React.useMemo(() => {
    const isMinute = typeof userBucketSize === 'number' || userBucketSize.unit === 'm';
    if (isMultiDay && isMinute) return DEFAULT_MULTI_DAY_BUCKET;
    if (!isMultiDay && !isMinute) return DEFAULT_SINGLE_DAY_BUCKET;
    return userBucketSize;
  }, [isMultiDay, userBucketSize]);

  // When the table is filtered to a single ticker (e.g. historical page with
  // tickers="TSLA"), SPX is absent from the dataset, so the reference line
  // would render as a flat zero. Detect that case and use the filtered ticker
  // as the reference instead. Skip nullish tickers so a stray empty value at
  // index 0 doesn't trump genuinely-uniform data.
  const singleTicker = React.useMemo(() => {
    let found: string | null = null;
    for (const t of trades) {
      if (!t.ticker) continue;
      if (found === null) found = t.ticker;
      else if (t.ticker !== found) return null;
    }
    return found;
  }, [trades]);

  const referenceTicker = singleTicker || REFERENCE_TICKER;
  const referenceLabel = singleTicker ? `${singleTicker} Price` : 'SPX (S&P 500)';
  const priceButtonLabel = singleTicker || 'S&P 500';

  // All-ticker aggregation drives the call/put areas — no ticker filter so flow
  // values reflect the whole table. The `price` field on each bucket here is
  // noisy (last spot from any ticker), and is overwritten below.
  const bucketedData = useBucketedFlowData(trades, bucketSize, metric, undefined, displayTimezone);

  // Reference-ticker aggregation gives us the spot per bucket on the same time
  // grid. We only need its `price` field; flow values are discarded. Source
  // from `referenceTrades` (unfiltered) so the line survives table presets
  // like EQUITIES that strip indices. When filtered to a single ticker we
  // sample that ticker from `trades` directly.
  const referenceBuckets = useBucketedFlowData(
    singleTicker ? trades : (referenceTrades && referenceTrades.length > 0 ? referenceTrades : trades),
    bucketSize,
    metric,
    referenceTicker,
    displayTimezone,
  );

  const data = React.useMemo(() => {
    const priceByLabel = new Map<string, number>();
    referenceBuckets.forEach((b) => {
      if (b.price > 0) priceByLabel.set(b.bucketLabel, b.price);
    });

    let lastPrice = 0;
    const merged = bucketedData.map((b) => {
      const price = priceByLabel.get(b.bucketLabel);
      if (price && price > 0) lastPrice = price;
      return { ...b, price: lastPrice };
    });

    // Fill any leading buckets that preceded the first SPX trade.
    backfillLeadingPrices(merged);
    return merged;
  }, [bucketedData, referenceBuckets]);

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
      <FlowGraph
        data={data}
        metric={metric}
        bucketSize={bucketSize}
        bucketOptions={bucketOptions}
        onMetricChange={setMetric}
        onBucketSizeChange={setUserBucketSize}
        mode={mode}
        onModeChange={setMode}
        chartMinHeight={0}
        referenceLabel={referenceLabel}
        priceButtonLabel={priceButtonLabel}
      />
    </div>
  );
}
